/**
 * Tenant-aware tutor UI labels for Phase 1B.
 *
 * Resolution uses trusted school language settings from GET /me/school-language
 * (ui_default, allowed locales, preferred/default dialect keys).
 * Never derive labels from school display name or query-string tenant ids.
 */

export type TutorTenantLanguageHint = {
  ui_default?: string | null;
  allowed?: string[] | null;
  preferred_dialect_keys?: string[] | null;
  default_dialect_key?: string | null;
};

export type TutorTenantLabels = {
  /** True when school language config indicates Malay (ms / malay_*). */
  isMalay: boolean;
  languageCode: 'ms' | 'ta';
  locale: string;
  dialectKey: string;
  languageDisplayName: string;
  tutorDisplayName: string;
  moduleTitleAiTutor: string;
  moduleTitleCommunicationTutor: string;
  progressTitle: string;
  practiceCta: string;
  activityTitle: string;
  emptyLessonsMessage: string;
  progressEmptyHint: string;
  keepPractisingHint: string;
  skillsLoadingLabel: string;
  openTutorLinkLabel: string;
  positiveGuidance: string;
  recommendationEmptyHint: string;
};

export const TAMIL_TUTOR_TENANT_LABELS: TutorTenantLabels = {
  isMalay: false,
  languageCode: 'ta',
  locale: 'ta',
  dialectKey: 'standard_tamil',
  languageDisplayName: 'Standard Tamil',
  tutorDisplayName: 'Tamil Teacher',
  moduleTitleAiTutor: 'Tamil Tutor',
  moduleTitleCommunicationTutor: 'Communication Tutor',
  progressTitle: 'Your Tamil Tutor skills',
  practiceCta: 'Practice in Tamil Tutor',
  activityTitle: 'Your Tamil Tutor activity',
  emptyLessonsMessage:
    'No Tamil Tutor lessons are assigned to your school yet. Please contact your school administrator.',
  progressEmptyHint:
    'Your Tamil Tutor progress will show up here after you finish a lesson. Ask your teacher if you are not sure which lesson to try first.',
  keepPractisingHint: 'Keep practicing in Tamil Tutor — your star skills will show up here soon!',
  skillsLoadingLabel: 'Loading your Tamil Tutor skills…',
  openTutorLinkLabel: 'Open Tamil Tutor →',
  positiveGuidance: 'Awesome — no focus areas right now. Keep enjoying Tamil Tutor!',
  recommendationEmptyHint: 'Finish a Tamil Tutor lesson to see personalized tips and focus areas here.',
};

export const MALAY_TUTOR_TENANT_LABELS: TutorTenantLabels = {
  isMalay: true,
  languageCode: 'ms',
  locale: 'ms-SG',
  dialectKey: 'malay_baku_sg',
  languageDisplayName: 'Bahasa Melayu Baku',
  tutorDisplayName: 'Cikgu AIn',
  moduleTitleAiTutor: 'AI Lesson Tutor',
  moduleTitleCommunicationTutor: 'Narrative Writing Tutor',
  progressTitle: 'Your Malay Learning Progress',
  practiceCta: 'Continue Learning',
  activityTitle: 'Your AI Lesson Tutor activity',
  emptyLessonsMessage:
    'No AI Lesson Tutor lessons are assigned to your school yet. Please contact your school administrator.',
  progressEmptyHint:
    'Your Malay Learning Progress will show up here after you finish a lesson. Ask your teacher if you are not sure which activity to try first.',
  keepPractisingHint: 'Keep learning with your AI Lesson Tutor — you are building great skills!',
  skillsLoadingLabel: 'Loading your Malay Learning Progress…',
  openTutorLinkLabel: 'Open AI Lesson Tutor →',
  positiveGuidance: 'Awesome — no focus areas right now. Keep learning with your AI Lesson Tutor!',
  recommendationEmptyHint:
    'Finish an AI Lesson Tutor activity to see personalized tips and focus areas here.',
};

function normalizeToken(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/_/g, '-');
}

/** True when a locale/ui code indicates Malay (ms, ms-SG, ms-MY, …). */
export function isMalayLanguageCode(value: string | null | undefined): boolean {
  const token = normalizeToken(value);
  if (!token) return false;
  return token === 'ms' || token.startsWith('ms-');
}

/** True when a dialect key indicates Malay (malay_baku_sg, malay_*, …). */
export function isMalayDialectKey(value: string | null | undefined): boolean {
  const raw = String(value || '')
    .trim()
    .toLowerCase();
  if (!raw) return false;
  return raw === 'malay_baku_sg' || raw.startsWith('malay_');
}

function normalizeDialectToken(value: string | null | undefined): string {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function isExplicitNonMalayLanguageCode(value: string | null | undefined): boolean {
  const token = normalizeToken(value);
  if (!token) return false;
  return !isMalayLanguageCode(token);
}

function isExplicitNonMalayDialectKey(value: string | null | undefined): boolean {
  const token = normalizeDialectToken(value);
  if (!token) return false;
  return !isMalayDialectKey(token);
}

/**
 * Detect Malay from trusted school language settings only.
 * Query params / school names are intentionally ignored.
 *
 * Precedence (Phase 1C hardening):
 * 1. Explicit ui_default is ms / ms-*
 * 2. Explicit default_dialect_key is Malay
 * 3. Single unambiguous allowed language is Malay (no explicit non-Malay ui_default)
 * 4. Single unambiguous preferred dialect is Malay (no explicit non-Malay default dialect)
 * 5. Otherwise Tamil/default behaviour
 */
export function isMalaySchoolLanguage(hint: TutorTenantLanguageHint | null | undefined): boolean {
  if (!hint) return false;

  if (isMalayLanguageCode(hint.ui_default)) return true;

  if (isMalayDialectKey(hint.default_dialect_key)) return true;

  const allowed = (hint.allowed ?? []).map((code) => normalizeToken(code)).filter(Boolean);
  if (
    allowed.length === 1 &&
    isMalayLanguageCode(allowed[0]) &&
    !isExplicitNonMalayLanguageCode(hint.ui_default)
  ) {
    return true;
  }

  const preferredDialects = (hint.preferred_dialect_keys ?? [])
    .map((dialect) => normalizeDialectToken(dialect))
    .filter(Boolean);
  if (
    preferredDialects.length === 1 &&
    isMalayDialectKey(preferredDialects[0]) &&
    !isExplicitNonMalayDialectKey(hint.default_dialect_key)
  ) {
    return true;
  }

  return false;
}

/**
 * Resolve tutor UI labels.
 * Precedence: school language config → Tamil/default fallback.
 */
export function resolveTutorTenantLabels(
  hint: TutorTenantLanguageHint | null | undefined
): TutorTenantLabels {
  if (isMalaySchoolLanguage(hint)) {
    return MALAY_TUTOR_TENANT_LABELS;
  }
  return TAMIL_TUTOR_TENANT_LABELS;
}

/** Remap known Tamil empty-lesson copy to the active tenant message.
 * Returns null when there is no error message (do not invent an error for empty TOC).
 */
export function mapLessonsEmptyMessageForTenant(
  message: string | null | undefined,
  labels: TutorTenantLabels
): string | null {
  const text = String(message || '').trim();
  if (!text) return null;
  if (
    text === TAMIL_TUTOR_TENANT_LABELS.emptyLessonsMessage ||
    text.includes('No Tamil Tutor lessons are assigned')
  ) {
    return labels.emptyLessonsMessage;
  }
  return text;
}

/** Dialect option display name for the live header (UI only; does not seed dialects). */
export function resolveDialectDisplayName(
  optionName: string | null | undefined,
  labels: TutorTenantLabels
): string {
  if (labels.isMalay) return labels.languageDisplayName;
  const name = String(optionName || '').trim();
  return name || labels.languageDisplayName;
}
